import { useState, useEffect, useCallback, useMemo } from 'react';
import { Users as UsersIcon, AlertTriangle, Check } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { getTeams } from '../services/teamService';
import { getUsers } from '../services/userService';
import { assignManagerTeams } from '../services/membershipService';
import './ManagerAssignment.css';

const MIN_TEAMS = 1;
const MAX_TEAMS = 20;

/**
 * ManagerAssignment Page (Task 13.1)
 *
 * Admin-only page that assigns a single manager to multiple teams in one
 * transaction.
 *
 * - Multi-select populated from active teams (GET /api/v1/teams?status=active) (R6.3)
 * - Disabled with a help message when fewer than 2 active teams exist (R6.5)
 * - Submits 2-20 distinct team_id values to
 *   POST /api/v1/users/{user_id}/team-assignment (R6.1, R6.6)
 * - Surfaces the failing team_id when the backend rejects the request (R6.2)
 */
export default function ManagerAssignment() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [toast, setToast] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [teamsResponse, usersResponse] = await Promise.all([
        getTeams({ status: 'active' }),
        getUsers({ limit: 1000 })
      ]);
      // Defensive: only keep active teams even if the API echoes others.
      setTeams((teamsResponse.data || []).filter((t) => t.status === 'active'));
      setUsers(usersResponse.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load assignment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const enoughTeams = teams.length >= MIN_TEAMS;
  const selectionCount = selectedTeamIds.length;

  // Selection must be 2-20 distinct teams and a user must be chosen (R6.6).
  const selectionValid = useMemo(() => {
    const distinct = new Set(selectedTeamIds);
    return (
      selectedUserId !== '' &&
      distinct.size >= MIN_TEAMS &&
      distinct.size <= MAX_TEAMS &&
      distinct.size === selectedTeamIds.length
    );
  }, [selectedUserId, selectedTeamIds]);

  const handleMultiSelectChange = (e) => {
    setSubmitError(null);
    const values = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setSelectedTeamIds(values);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const distinctTeamIds = Array.from(new Set(selectedTeamIds));
    if (selectedUserId === '') {
      setSubmitError('Select a user to assign.');
      return;
    }
    if (distinctTeamIds.length < MIN_TEAMS || distinctTeamIds.length > MAX_TEAMS) {
      setSubmitError(`Select between ${MIN_TEAMS} and ${MAX_TEAMS} teams.`);
      return;
    }

    try {
      setSubmitting(true);
      await assignManagerTeams(selectedUserId, distinctTeamIds);
      const userLabel = users.find((u) => u.id === selectedUserId)?.name || selectedUserId;
      setToast({
        message: `Assigned ${userLabel} to ${distinctTeamIds.length} teams`,
        type: 'success'
      });
      setSelectedUserId('');
      setSelectedTeamIds([]);
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      // The backend identifies the failing team_id on rollback (R6.2).
      const failingTeamId =
        data?.team_id || data?.failing_team_id || data?.error?.team_id || null;

      if (failingTeamId) {
        setSubmitError(
          `Assignment failed for team "${failingTeamId}": ${data?.message || 'team failed validation'}. No teams were assigned.`
        );
      } else if (status === 400) {
        setSubmitError(
          data?.message || `Select between ${MIN_TEAMS} and ${MAX_TEAMS} teams.`
        );
      } else {
        setSubmitError(data?.message || err.message || 'Failed to assign teams.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="manager-assignment-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <UsersIcon size={32} />
              Manager Assignment
            </h1>
            <p className="page-subtitle">
              Assign a manager to multiple teams in a single operation
            </p>
          </div>
        </div>
      </div>

      <Card className="manager-assignment-card">
        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} onRetry={loadData} />}

        {!loading && !error && (
          <form onSubmit={handleSubmit} className="assignment-form" noValidate>
            <div className="form-group">
              <label htmlFor="user-select">User</label>
              <select
                id="user-select"
                value={selectedUserId}
                onChange={(e) => { setSubmitError(null); setSelectedUserId(e.target.value); }}
                disabled={submitting}
                className="assignment-select"
              >
                <option value="">Select a user…</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="team-multiselect">
                Teams <span className="optional-label">(select {MIN_TEAMS}–{MAX_TEAMS})</span>
              </label>

              {!enoughTeams ? (
                <div className="assignment-help" role="status">
                  <AlertTriangle size={18} />
                  <span>
                    At least {MIN_TEAMS} active team is required to assign a user.
                    There are currently {teams.length} active teams.
                  </span>
                </div>
              ) : (
                <>
                  <select
                    id="team-multiselect"
                    multiple
                    value={selectedTeamIds}
                    onChange={handleMultiSelectChange}
                    disabled={!enoughTeams || submitting}
                    className="assignment-multiselect"
                    size={Math.min(Math.max(teams.length, 4), 10)}
                  >
                    {teams.map((team) => (
                      <option key={team.team_id} value={team.team_id}>
                        {team.name} ({team.team_id})
                      </option>
                    ))}
                  </select>
                  <p className="field-hint">
                    Hold Ctrl (Cmd on macOS) to select multiple teams.{' '}
                    {selectionCount} selected.
                  </p>
                </>
              )}
            </div>

            {submitError && (
              <div className="assignment-error" role="alert">
                <AlertTriangle size={18} />
                <span>{submitError}</span>
              </div>
            )}

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={!enoughTeams || !selectionValid || submitting}
                icon={<Check size={18} />}
              >
                {submitting ? 'Assigning…' : 'Assign Teams'}
              </Button>
            </div>
          </form>
        )}
      </Card>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}
